import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useRequest from "../../../components/hooks/use-request";
import Navbar from "../../../components/layout/navbar";
import Icon from "../../../assets/icons";
import { showToast } from "../../../components/toast";
import { I_ProductData, I_Profile } from "../../inventory/types";
import { capitalizeFirstLetter } from "../../../utilz/function";
import { RingLoader } from "react-spinners";

import AddOrderDialog from "../../../components/orders";
import { I_Customer } from "../../customers/types";
import { I_PurchaseData } from "../types";
import ActionModal from "./action-modal";
import Button from "../../../components/button";
import { Plus } from "lucide-react";
import PurchaseOrderTable from "./items-table";
import AddOrderNoteDialog from "../../../components/purchase-order-note";
import GRNReceipt from "../../../components/purchase-order-note/GRN-recepit";
import FIlterSelect from "../../../components/filter-select";

const PurchaseDetails = () => {
  const [purchase, setPurchase] = useState<I_PurchaseData | null>(null);
  const { id } = useParams<{ id: string }>();
  const [ordersModal, setOrdersModal] = useState(false);
  const [orderNote, setOrderNote] = useState(false)
  const [grnReceipt, setGRNReceipt] = useState(false)
  const [customer, setCustomer] = useState<I_Customer[]>([]);
  const [product, setProduct] = useState<I_ProductData[]>([]);
  const [selectedType, setSelectedType] = useState("");
  const navigate = useNavigate();
  const [modalConfig, setModalConfig] = useState<{
    isVisible: boolean;
    actionType: "approve" | "delete";
  }>({
    isVisible: false,
    actionType: "approve",
  });

  const [profile, setProfile] = useState<I_Profile | null>(null);
  const userToken = localStorage.getItem("token");
  const { makeRequest: getProfile } = useRequest("/users/profile", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const { makeRequest: getOrdersById } = useRequest(
    `/purchase-order/${id}`,
    "GET"
  );

  const { makeRequest: deleteOrders } = useRequest(
    `/purchase-order/${id}`,
    "DELETE",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const { makeRequest: approveOrders, loading } = useRequest(
    `/purchase-order/${id}`,
    "PUT",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const { makeRequest: getCustomers } = useRequest("/customers", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: getProducts } = useRequest("/inventory", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: UpdateOrdersStatus } = useRequest(
      `/purchase-order/${id}`,
      "PUT"
  );


  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      const [response] = await getOrdersById();
      setPurchase(response?.data || null);
    };
    fetchPurchaseOrders();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      const [response] = await getProfile();
      if (response.status) {
        setProfile(response.data?.user);
      }
    };
    fetchProfile();
  }, []);

  const handleConfirmAction = async () => {
    if (modalConfig.actionType === "approve") {
      const approval = {
        status: "approved",
      };
      const [response] = await approveOrders(approval);
      if (response?.status) {
        showToast(response.message, true);
        window.location.reload();
      } else {
        showToast(response?.message, false);
      }
    } else if (modalConfig.actionType === "delete") {
      const [response] = await deleteOrders();
      if (response?.status) {
        showToast(response.message, true);

        navigate("/dashboard/purchase-orders");
      } else {
        showToast(response?.message, false);
      }
    }
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      const [response] = await getCustomers();
      if (response.status) {
        setCustomer(response.data?.customers?.docs);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      const [response] = await getProducts();
      if (response.status) {
        setProduct(response.data?.inventory?.docs);
      }
    };
    fetchProducts();
  }, []);

  const handleApprove = () => {
    setModalConfig({
      isVisible: true,
      actionType: "approve",
    });
  };


  const handleDelete = () => {
    setModalConfig({
      isVisible: true,
      actionType: "delete",
    });
  };

  function closeOrdersModal() {
    setOrdersModal(false);
  }

  function openOrdersModal() {
    setOrdersModal(true);
  }

  function closeOrderNote () {
    setOrderNote(false);
  }
  function openOrderNote () {
    setOrderNote(true);
  }

  function closeGRNReceipt() {
    setGRNReceipt(false);
  }
  function openGRNReceipt() {
    setGRNReceipt(true);
  }

  const formatCurrency = (amount: any) => {
    return `₦${amount?.toLocaleString()}`;
  };

  const handleChangeStatus = async (selectedValue: string) => {
      setSelectedType(selectedValue)

      const [response] = await UpdateOrdersStatus({status: selectedValue})
      if (response?.status) {
          showToast(response.message, true);
          window.location.reload();
        } else {
          showToast(response?.message, false);
        }
  }  

  // Check if purchase status is shipped
  const isShipped = purchase?.status === "shipped";

  // Helper function to check if purchase note is empty
  const isPurchaseNoteEmpty = () => {
    if (!purchase?.note) return true;
    
    const { deliveryPoint, loadingPoint, deliveryNote, transportationCost, packagingCost, miscCost } = purchase.note;
    
    // Check if all key fields are empty or null/undefined
    return (
      (!deliveryPoint || deliveryPoint.trim() === "") &&
      (!loadingPoint || loadingPoint.trim() === "") &&
      (!deliveryNote || deliveryNote.trim() === "") &&
      (!transportationCost || transportationCost === 0) &&
      (!packagingCost || packagingCost === 0) &&
      (!miscCost || miscCost === 0)
    );
  };

 
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as I_Profile} />

      <main className="flex-1 container mx-auto py-6">

      
      <div className="flex justify-between gap-5 mt-5 mx-10">
        <div className="flex gap-4">
          <div
            onClick={() => navigate(-1)}
            className="flex gap-2 font-semibold text-[16px] border-r-2 pr-4 border-separate cursor-pointer "
          >
            <Icon name="appbackIcon" className="mt-1" /> Back
          </div>

          <h1 className="md:text-[30px] -mt-2 font-bold">
            {purchase ? capitalizeFirstLetter(purchase.poId) : "Loading..."}
          </h1>
        </div>

        <div className="text-[14px] flex gap-2">
          {/* Always show Edit and Delete buttons when not shipped */}
          {!isShipped && (
            <>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                className="font-normal md:mt-0 mt-6"
                onClick={openOrdersModal}
              >
                <Icon name="editIconBlack" /> Edit
              </Button>

               
              <FIlterSelect
                    name="status"
                    options={[
                        { value: "pending", label: "Pending" },
                        { value: "approved", label: "Approved" },
                        { value: "shipped", label: "Shipped" },
                        { value: "received", label: "Received" },
                        { value: "cancelled", label: "Cancelled" },
                        
                    ]}
                    className="mt-2 md:mt-0 w-full "
                    icon="filterIcon"
                    placeholder="Update delivery status"
                    onChange={handleChangeStatus}
                    value={purchase?.status}
              />

            

              <Button
                size="sm"
                variant="tertiary"
                type="button"
                className="font-normal md:mt-0 mt-6"
                onClick={handleDelete}
              >
                <Icon name="markIcon" /> Delete
              </Button>
            </>
          )}
          {isShipped && (
            <>
              {/* <Button
                size="sm"
                variant="secondary"
                type="button"
                className="font-normal md:mt-0 mt-6"
                onClick={openOrdersModal}
              >
                <Icon name="editIconBlack" /> Edit
              </Button> */}

               
              
            

              {/* <Button
                size="sm"
                variant="tertiary"
                type="button"
                className="font-normal md:mt-0 mt-6 w-full"
                onClick={handleDelete}
              >
                <Icon name="markIcon" /> Delete
              </Button> */}
              <Button
                size="sm"
                variant="primary"
                type="button"
                className="font-normal md:mt-0 mt-6 w-full"
                onClick={openGRNReceipt}
              >
                <Plus /> Create GRN
              </Button>
            </>
          )}

          {/* If note is empty, only show Create Order Note button (along with Edit/Delete above) */}
          {isPurchaseNoteEmpty() && purchase?.status !== "approved" && !isShipped && (
            <Button
              size="lg"
              variant="primary"
              type="button"
              className="font-normal md:mt-0 mt-6"
              onClick={openOrderNote}
            >
              <Plus />
              Create Order Note
            </Button>
          )}
          
          {/* Show Approve button only when note is NOT empty, status is not approved, and not shipped */}
          {!isPurchaseNoteEmpty() && purchase?.status !== "approved" && !isShipped && (
            <Button
              size="sm"
              variant="primary"
              type="button"
              className="font-normal md:mt-0 mt-6"
              onClick={handleApprove}
            >
              <Icon name="cancelIcon" /> Approve
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <RingLoader color="#ffffff" loading={loading} size={20} />
      ) : (
        <ActionModal
          visible={modalConfig.isVisible}
          onCancel={() => setModalConfig({ ...modalConfig, isVisible: false })}
          onConfirm={handleConfirmAction}
          title={`Sales Order ${
            modalConfig.actionType === "approve" ? "Approval" : "Delete"
          }`}
          content={`Are you sure you want to ${
            modalConfig.actionType
          } sales order ${""}?`}
          actionType={modalConfig.actionType}
        />
      )}
      
      <AddOrderNoteDialog
        isVisible ={orderNote}
        onClose={closeOrderNote}
        purchase = {purchase}
      
      />

      <GRNReceipt
        isVisible={grnReceipt}
        onClose={closeGRNReceipt}
        purchase = {purchase}
      
      
      />
      

      <AddOrderDialog
        isVisible={ordersModal}
        onClose={closeOrdersModal}
        customers={customer}
        product={product}
      />
      <div className="grid grid-cols-3 gap-6 mt-5 mx-10 mb-20">
        <div className="col-span-2">
          <PurchaseOrderTable data={purchase ?? []} />

          <div className="py-3 bg-white rounded-3xl shadow border w-full border-[#C9C9C9] pt-5 mt-7">
            <div className="">
              <div className="border-b pb-5 mt-5 px-5">
                <p className="text-[20px] font-bold">Purchase Order Note</p>
              </div>
              

              <div className="p-4 space-y-3 border-b pb-5 mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Transportation Cost</span>
                  <span className="font-medium">{formatCurrency(purchase?.note?.transportationCost)}</span>
                </div>

              </div>
              <div className="p-4 space-y-3 border-b pb-5 mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Packing Cost</span>
                  <span className="font-medium">{formatCurrency(purchase?.note?.packagingCost)}</span>
                </div>

              </div>
              <div className="p-4 space-y-3 border-b pb-5 mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Miscellaneous Cost</span>
                  <span className="font-medium">{formatCurrency(purchase?.note?.miscCost)}</span>
                </div>

              </div>
              <div className=" space-y-3  mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Total</span>
                  <span className="font-medium"> {formatCurrency(
                    (purchase?.note?.transportationCost || 0) + 
                    (purchase?.note?.packagingCost || 0) + 
                    (purchase?.note?.miscCost || 0)
                  )}</span>
                </div>

              </div>

            </div>
          </div>
          <div className="py-3 bg-white rounded-3xl shadow border w-full border-[#C9C9C9] pt-5 mt-7">
            <div className="">
              <div className="border-b pb-5 mt-5 px-5">
                <p className="text-[20px] font-bold">Final Total</p>
              </div>
              

              <div className="p-4 space-y-3 border-b pb-5 mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Purchase Order Total</span>
                  <span className="font-medium">{formatCurrency(purchase?.note?.transportationCost)}</span>
                </div>

              </div>
              <div className="p-4 space-y-3 border-b pb-5 mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Purchase Note Total</span>
                  <span className="font-medium">{formatCurrency(purchase?.note?.packagingCost)}</span>
                </div>

              </div>
              
              <div className=" space-y-3 mt-5 px-5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Total</span>
                  <span className="font-medium"> {formatCurrency(
                    (purchase?.note?.transportationCost || 0) + 
                    (purchase?.note?.packagingCost || 0) + 
                    (purchase?.note?.miscCost || 0)
                  )}</span>
                </div>

              </div>

            </div>
          </div>
        </div>

        <div className="col-span-1">
          <div className="bg-white shadow border w-full border-[#C9C9C9] rounded-lg">
            <h2 className="text-lg font-semibold mb-4 px-6 pt-3 ">
              Purchase Order Details
            </h2>
            <div className="border-t pt-4 p-6 mt-5">
              <div className="flex flex-col gap-4">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Requisition ID</p>
                    <p className="font-medium capitalize">{purchase?.poId}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Sourcing Officer</p>
                    <p className="font-medium capitalize">{purchase?.sourcingOfficer}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="users" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Supplier</p>
                    <p className="font-medium capitalize">{purchase?.supplier?.name}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="calender" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Date created</p>
                    <p className="font-medium capitalize">{purchase?.createdAt ? new Date(purchase.createdAt).toLocaleDateString("en-US") : 'N/A'}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="calender" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Expected Date of Delivery </p>
                    <p className="font-medium capitalize">{purchase?.createdAt ? new Date(purchase.purchaseRequisition?.dateRequired).toLocaleDateString("en-US") : 'N/A'}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="status" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Status</p>
                    <p className="font-medium capitalize">{purchase?.status}</p>
                  </div>
                </div>
              </div>
            
            </div>

          

          
          </div>

          <div className="bg-white shadow border w-full border-[#C9C9C9]  rounded-lg mt-7">
            <h2 className="text-lg font-semibold mb-4 px-6 pt-3 ">
              Purchase Note Details
            </h2>
            <div className="border-t pt-4 p-6 mt-5">
              <div className="flex flex-col gap-4">
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="status" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Loading Point</p>
                    <p className="font-medium capitalize">{purchase?.note?.loadingPoint}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="status" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Delivery Point</p>
                    <p className="font-medium capitalize">{purchase?.note?.deliveryPoint}</p>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="bg-[#E8F2E8] rounded-full p-3">
                    <Icon name="infoIcon" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Delivery Note</p>
                    <p className="font-medium capitalize">{purchase?.note?.deliveryNote}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    </div>
  );
};

export default PurchaseDetails;